import { useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import QuothaAvatar from "../components/quotha/QuothaAvatar"
import { usePageMetadata } from '../hooks/usePageMetadata'

const QUOTHA_GUEST_BASE = "/guest/quotha"
const figuresPlaceholderImage = "/images/chat/figures-talking.webp"
const authPlaceholderImage = "/images/chat/login-icon-clean.webp"

export default function ChatHub() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const figureSlugFromQuery = searchParams.get("figure_slug")?.trim()
  const placeSlugFromQuery = searchParams.get("place_slug")?.trim()

  useEffect(() => {
    if (!figureSlugFromQuery) {
      return
    }

    const encodedFigure = encodeURIComponent(figureSlugFromQuery)
    const target = placeSlugFromQuery
      ? `/guest/${encodedFigure}?place_slug=${encodeURIComponent(placeSlugFromQuery)}`
      : `/guest/${encodedFigure}`

    navigate(target, { replace: true })
  }, [figureSlugFromQuery, placeSlugFromQuery, navigate])

  const quothaGuestPath = placeSlugFromQuery
    ? `${QUOTHA_GUEST_BASE}?place_slug=${encodeURIComponent(placeSlugFromQuery)}`
    : QUOTHA_GUEST_BASE

  usePageMetadata({
    title: 'AI History Chat | Places in Time',
    description:
      'Ask questions about historical figures and places through guided, evidence-led AI conversations designed for curiosity, learning, and exploration.',
    path: '/chat',
  })

  return (
    <main className="chat-hub">
      <section className="chat-hub-hero">
        <div className="chat-hub-hero-inner">
          <h1>Talk to History — With Care and Character</h1>

          <p className="chat-hub-lede">
            Places in Time isn’t a generic AI chatbot playing dress-up. Here, you can speak with
            historical figures and explore places through an AI-powered historical atmosphere —
            shaped by real events, period perspective, and adjustable historical boundaries.
            Conversations can be tuned for tone and depth, while Quotha, our archivist raven,
            quietly helps keep the experience grounded when it matters.
          </p>

          <p className="chat-hub-early">
            Places in Time is in <strong>early access</strong>. What you try, what you notice, and
            your feedback will directly influence how and what we build next.
          </p>
        </div>
      </section>

      <section className="chat-hub-grid">
        <article className="chat-card chat-card-quotha">
          <h2>Meet Quotha</h2>
          <p>Quotha is the scholarly raven of Places in Time — curious, sharp, and intolerant of lazy claims.</p>
          <div className="chat-card-media chat-card-media-slot" aria-hidden="true">
            <QuothaAvatar size={190} />
          </div>
          <div className="chat-card-footer">
            <Link className="chat-card-button" to={quothaGuestPath}>
              Start a guest chat
            </Link>
            <p className="chat-card-note">
              Prefer the dossier? <Link to="/people/quotha">Visit Quotha’s profile</Link>.
            </p>
          </div>
        </article>

        <article className="chat-card">
          <h2>Chat with historical figures</h2>
          <p>Choose a figure, open their page, then hit the chat button to begin.</p>
          <div className="chat-card-media chat-card-media-slot">
            <img
              src={figuresPlaceholderImage}
              alt="Illustration of historical figures talking in a study"
              loading="lazy"
            />
          </div>
          <div className="chat-card-footer">
            <Link className="chat-card-button" to="/people">
              Choose a figure
            </Link>
            <p className="chat-card-note" aria-hidden="true">
              &nbsp;
            </p>
          </div>
        </article>

        <article className="chat-card">
          <h2>Log in or register</h2>
          <p>Create an account to save chats, manage threads, and unlock deeper controls.</p>
          <div className="chat-card-media chat-card-media-slot">
            <img src={authPlaceholderImage} alt="Login and registration icon" loading="lazy" />
          </div>
          <div className="chat-card-footer">
            <div className="chat-cta-row">
              <Link className="chat-card-button chat-card-button-secondary" to="/login">
                Log in
              </Link>
              <Link className="chat-card-button" to="/register">
                Register
              </Link>
            </div>
            <p className="chat-card-note" aria-hidden="true">
              &nbsp;
            </p>
          </div>
        </article>
      </section>

      <section className="chat-hub-footer">
        <p className="chat-hub-footer-lede">Prefer to explore places first?</p>
        <div className="chat-hub-footer-cta">
          <Link className="chat-hub-footer-link" to="/places/time-map">
            Try the Time Map
          </Link>
          <Link className="chat-hub-footer-link chat-hub-footer-link-secondary" to="/places">
            Browse all places
          </Link>
        </div>
      </section>
    </main>
  )
}
